import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, Calendar, DollarSign, BarChart3, Target, Zap, Filter, Search, ChevronUp, ChevronDown, X, Download } from 'lucide-react';

const DecisionReport = () => {
  const [allReports, setAllReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const baseUrl = process.env.REACT_APP_SERVER_URL;
  const apiUrl = `${baseUrl}/api/Decision-report`;
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [decisionFilter, setDecisionFilter] = useState('all');
  const [uniqueSymbols, setUniqueSymbols] = useState([]);
  const [uniqueDecisions, setUniqueDecisions] = useState([]);
  
  // Sort states
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    setLoading(true);
    fetch(apiUrl)
      .then(res => res.json())
      .then(data => {
        setAllReports(data);
        setFilteredReports(data);
        calculateSummary(data);
        setUniqueSymbols([...new Set(data.map(r => r.symbol))].sort());
        setUniqueDecisions([...new Set(data.map(r => r.decision))].sort());
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching data:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    applyFilters();
  }, [dateFilter, customStartDate, customEndDate, symbolFilter, decisionFilter, allReports, sortField, sortDirection]);

  const applyFilters = () => {
    let filtered = [...allReports];
    
    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let startDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case '1month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case '3months':
          startDate.setMonth(now.getMonth() - 3);
          break;
        case '6months':
          startDate.setMonth(now.getMonth() - 6);
          break;
        case '1year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        case 'custom':
          if (customStartDate && customEndDate) {
            startDate = new Date(customStartDate);
            const endDate = new Date(customEndDate);
            filtered = filtered.filter(r => {
              const tradeDate = new Date(r.trandate);
              return tradeDate >= startDate && tradeDate <= endDate;
            });
          }
          break;
        default:
          break;
      }
      
      if (dateFilter !== 'custom') {
        filtered = filtered.filter(r => new Date(r.trandate) >= startDate);
      }
    }
    
    // Symbol filter
    if (symbolFilter) {
      filtered = filtered.filter(r => 
        r.symbol.toLowerCase().includes(symbolFilter.toLowerCase())
      );
    }
    
    // Decision filter
    if (decisionFilter !== 'all') {
      filtered = filtered.filter(r => r.decision === decisionFilter);
    }
    
    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        
        // Handle numeric fields
        if (['qty', 'buy_rate', 'sell_price', 'ltp', 'pl', 'pl_percent', 'age'].includes(sortField)) {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }
        
        // Handle date fields
        if (['trandate', 'timestamp'].includes(sortField)) {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        
        // Handle string fields
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
        }
        if (typeof bVal === 'string') {
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    setFilteredReports(filtered);
    calculateSummary(filtered);
  };

  const calculateSummary = (data) => {
    const goodExits = data.filter(r => r.decision === 'Good Exit').length;
    const missedProfits = data.filter(r => r.decision === 'Missed Profit').length;
    const earlyExits = data.filter(r => r.decision === 'Early Exit').length;
    
    // Calculate realized P&L (actual sold P&L)
    const realizedPL = data.reduce((sum, r) => {
      const buyAmount = parseFloat(r.qty) * parseFloat(r.buy_rate);
      const sellAmount = parseFloat(r.qty) * parseFloat(r.sell_price);
      return sum + (sellAmount - buyAmount);
    }, 0);
    
    // Calculate current P&L (if held at current price)
    const currentPL = data.reduce((sum, r) => sum + parseFloat(r.pl || 0), 0);
    
    // Calculate difference
    const plDifference = currentPL - realizedPL;
    
    // Calculate max days
    const maxDays = data.length > 0 ? Math.max(...data.map(r => parseInt(r.age || 0))) : 0;
    
    // Calculate success rate based on profit/loss
    const profitableTrades = data.filter(r => parseFloat(r.pl || 0) > 0).length;
    const successRate = data.length > 0 ? ((profitableTrades / data.length) * 100).toFixed(1) : 0;
    
    setSummary({
      total: data.length,
      goodExits,
      missedProfits,
      earlyExits,
      realizedPL,
      currentPL,
      plDifference,
      maxDays,
      successRate,
      profitableTrades
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setDateFilter('all');
    setSymbolFilter('');
    setDecisionFilter('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setSortField('');
    setSortDirection('asc');
  };

  const exportToCSV = () => {
    const headers = [
      'Symbol', 'Quantity', 'Buy Rate', 'Sell Price', 'Current Price', 
      'If Held P&L', 'P&L %', 'Decision', 'Age (Days)', 'Trade Date'
    ];
    
    const csvData = filteredReports.map(r => [
      r.symbol,
      r.qty,
      parseFloat(r.buy_rate).toFixed(2),
      parseFloat(r.sell_price).toFixed(2),
      parseFloat(r.ltp).toFixed(2),
      parseFloat(r.pl).toFixed(2),
      parseFloat(r.pl_percent).toFixed(2),
      r.decision,
      r.age,
      formatDate(r.trandate)
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `decision_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'Good Exit':
        return <CheckCircle size={14} className="text-success me-1" />;
      case 'Missed Profit':
        return <AlertTriangle size={14} className="text-warning me-1" />;
      case 'Early Exit':
        return <TrendingDown size={14} className="text-danger me-1" />;
      default:
        return <Target size={14} className="text-muted me-1" />;
    }
  };

  const getDecisionBadge = (decision) => {
    const badgeClass = {
      'Good Exit': 'bg-success',
      'Missed Profit': 'bg-warning text-dark',
      'Early Exit': 'bg-danger'
    }[decision] || 'bg-secondary';
    
    return (
      <span className={`badge ${badgeClass} d-flex align-items-center`} style={{ fontSize: '0.75rem' }}>
        {getDecisionIcon(decision)}
        {decision}
      </span>
    );
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp size={16} className="ms-1" /> : 
      <ChevronDown size={16} className="ms-1" />;
  };

  if (loading) {
    return (
      <div className="container-fluid py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading sales decision report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' ,marginTop: '56px' }}>
      {/* Header */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center">
              <BarChart3 className="me-2 text-primary" size={28} />
              <h2 className="mb-0 text-dark fw-bold">📉 Sales Decision Report</h2>
            </div>
            <button 
              className="btn btn-primary btn-sm"
              onClick={exportToCSV}
              disabled={filteredReports.length === 0}
            >
              <Download size={16} className="me-1" />
              Export CSV
            </button>
          </div>
          <p className="text-muted mb-0 small">Analyze trading decisions and identify opportunities</p>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body py-3">
              <div className="row g-2 align-items-end">
                {/* Date Filter Buttons */}
                <div className="col-md-4">
                  <label className="form-label small fw-semibold mb-1">Date Filter</label>
                  <div className="btn-group flex-wrap" role="group">
                    {[
                      { key: 'all', label: 'All' },
                      { key: 'today', label: 'Today' },
                      { key: '1month', label: '1M' },
                      { key: '3months', label: '3M' },
                      { key: '6months', label: '6M' },
                      { key: '1year', label: '1Y' },
                      { key: 'custom', label: 'Custom' }
                    ].map(btn => (
                      <button
                        key={btn.key}
                        type="button"
                        className={`btn btn-sm ${dateFilter === btn.key ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setDateFilter(btn.key)}
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Symbol Filter */}
                <div className="col-md-2">
                  <label className="form-label small fw-semibold mb-1">Symbol</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <Search size={16} />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search..."
                      value={symbolFilter}
                      onChange={(e) => setSymbolFilter(e.target.value)}
                    />
                  </div>
                </div>

                {/* Decision Filter */}
                <div className="col-md-2">
                  <label className="form-label small fw-semibold mb-1">Decision</label>
                  <select 
                    className="form-select"
                    value={decisionFilter}
                    onChange={(e) => setDecisionFilter(e.target.value)}
                  >
                    <option value="all">All Decisions</option>
                    {uniqueDecisions.map(decision => (
                      <option key={decision} value={decision}>{decision}</option>
                    ))}
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="col-md-2">
                  <button 
                    className="btn btn-outline-secondary btn-sm w-100"
                    onClick={clearFilters}
                  >
                    <X size={16} className="me-1" />
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Custom Date Range */}
              {dateFilter === 'custom' && (
                <div className="row g-2 mt-2">
                  <div className="col-md-3">
                    <label className="form-label small">Start Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">End Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards - Updated */}
      <div className="row mb-3">
        {[
          { key: 'total', label: 'Total', value: summary.total, icon: Target, color: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%)' },
          { key: 'goodExits', label: 'Good Exits', value: summary.goodExits, icon: CheckCircle, color: 'linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)' },
          { key: 'missedProfits', label: 'Missed', value: summary.missedProfits, icon: AlertTriangle, color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
          { key: 'realizedPL', label: 'Realized P&L', value: formatCurrency(summary.realizedPL), icon: DollarSign, color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
          { key: 'currentPL', label: 'Current P&L', value: formatCurrency(summary.currentPL), icon: TrendingUp, color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
          { key: 'plDifference', label: 'P&L Diff', value: formatCurrency(summary.plDifference), icon: TrendingDown, color: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
          { key: 'successRate', label: 'Success %', value: `${summary.successRate}%`, icon: Zap, color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
          { key: 'maxDays', label: 'Max Days', value: summary.maxDays, icon: Clock, color: 'linear-gradient(135deg, #fdbb2d 0%, #22c1c3 100%)' }
        ].map(card => (
          <div key={card.key} className="col-lg-3 col-md-4 col-6 mb-2">
            <div className="card border-0 shadow-sm h-100" style={{ background: card.color }}>
              <div className="card-body text-white text-center py-2">
                <card.icon size={15} className="mb-1" />
                <div className="small opacity-75">{card.label}</div>
                <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                  {(['realizedPL', 'currentPL', 'plDifference'].includes(card.key) && 
                    parseFloat(card.value.replace(/[₹,]/g, '')) < 0) ? 
                    <span className="text-warning">{card.value}</span> : card.value
                  }
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold text-dark">
                  <Filter size={16} className="me-1" />
                  Detailed Analysis ({filteredReports.length} records)
                </h6>
                <small className="text-muted">Click column headers to sort</small>
              </div>
            </div>
            <div className="card-body p-0">
              <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <table className="table table-hover mb-0" style={{ fontSize: '0.875rem' }}>
                  <thead className="sticky-top" style={{ backgroundColor: '#f8f9fa', zIndex: 10 }}>
                    <tr>
                      {[
                        { key: '', label: '#', sortable: false },
                        { key: 'symbol', label: 'Symbol', sortable: true },
                        { key: 'qty', label: 'Qty', sortable: true },
                        { key: 'buy_rate', label: 'Buy Rate', sortable: true },
                        { key: 'sell_price', label: 'SL Price', sortable: true },
                        { key: 'ltp', label: 'Current', sortable: true },
                        { key: 'pl', label: 'If Held P&L', sortable: true },
                        { key: 'pl_percent', label: 'P&L %', sortable: true },
                        { key: 'decision', label: 'Decision', sortable: true },
                        { key: 'age', label: 'Days', sortable: true },
                        { key: 'trandate', label: 'Date', sortable: true }
                      ].map(col => (
                        <th 
                          key={col.key} 
                          className={`border-0 fw-bold text-dark py-2 ${col.sortable ? 'user-select-none' : ''}`}
                          style={{ cursor: col.sortable ? 'pointer' : 'default', fontSize: '0.8rem' }}
                          onClick={() => col.sortable && handleSort(col.key)}
                        >
                          <div className="d-flex align-items-center">
                            {col.label}
                            {col.sortable && getSortIcon(col.key)}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReports.map((r, i) => {
                      const plClass = parseFloat(r.pl) >= 0 ? 'text-success' : 'text-danger';
                      const plPercentClass = parseFloat(r.pl_percent) >= 0 ? 'text-success' : 'text-danger';
                      const rowClass = i % 2 === 0 ? 'bg-light bg-opacity-25' : '';
                      
                      return (
                        <tr key={i} className={rowClass}>
                          <td className="py-2">
                            <span className="badge bg-primary" style={{ fontSize: '0.7rem' }}>{i + 1}</span>
                          </td>
                          <td className="py-2">
                            <div className="fw-bold text-primary small">{r.symbol}</div>
                          </td>
                          <td className="py-2">
                            <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>{r.qty}</span>
                          </td>
                          <td className="py-2">
                            <div className="fw-semibold small">₹{parseFloat(r.buy_rate).toFixed(2)}</div>
                          </td>
                          <td className="py-2">
                            <div className="fw-semibold text-danger small">₹{parseFloat(r.sell_price).toFixed(2)}</div>
                              <span className={`${parseFloat(r.ltp) > parseFloat(r.sell_price) ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.7rem' }}>
                                {((parseFloat(r.qty) * parseFloat(r.sell_price)) ).toFixed(1)}
                              </span>
                          </td>
                          <td className="py-2">
                            <div className="fw-semibold small">₹{parseFloat(r.ltp).toFixed(2)}</div>
                            <div className="d-flex align-items-center">
                              {parseFloat(r.ltp) > parseFloat(r.sell_price) ? 
                                <TrendingUp size={10} className="text-success me-1" /> : 
                                <TrendingDown size={10} className="text-danger me-1" />
                              }
                              <span className={`${parseFloat(r.ltp) > parseFloat(r.sell_price) ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.7rem' }}>
                                {((parseFloat(r.ltp) - parseFloat(r.sell_price)) / parseFloat(r.sell_price) * 100).toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="py-2">
                            <div className={`fw-bold small ${plClass}`}>
                              {formatCurrency(parseFloat(r.pl))}
                            </div>
                          </td>
                          <td className="py-2">
                            <div className={`fw-bold small ${plPercentClass}`}>
                              {parseFloat(r.pl_percent).toFixed(2)}%
                            </div>
                          </td>
                          <td className="py-2">
                            {getDecisionBadge(r.decision)}
                          </td>
                          <td className="py-2">
                            <div className="d-flex align-items-center">
                              <Calendar size={12} className="me-1 text-muted" />
                              <span className="fw-semibold small">{r.age}</span>
                            </div>
                          </td>
                          <td className="py-2">
                            <div className="small text-muted">{formatDate(r.trandate)}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionReport;